import React, { useRef, useState } from "react";
import { Check, ChevronsRight, ShieldCheck, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const HOLD_MS = 3000;

/**
 * Confirmación deliberada de "ya estoy a salvo" en 3 pasos (checkbox →
 * deslizar → mantener presionado) — a propósito con fricción, para que no
 * se resuelva una alerta real por accidente con un solo toque.
 */
export default function MarkSafeFlow({ alertId, bookingId, groupId, senderName, onResolved }) {
  const [step, setStep] = useState(1);
  const [checked, setChecked] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [holdProgress, setHoldProgress] = useState(0);
  const [holding, setHolding] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const trackRef = useRef(null);
  const timerRef = useRef(null);
  const startRef = useRef(0);

  const trackWidth = () => (trackRef.current ? trackRef.current.offsetWidth - 56 : 240);

  const onDragStart = (clientX) => {
    setDragging(true);
    trackRef.current._startX = clientX;
    trackRef.current._startDrag = dragX;
  };
  const onDragMove = (clientX) => {
    if (!dragging || !trackRef.current) return;
    const delta = clientX - trackRef.current._startX;
    const next = Math.max(0, Math.min(trackWidth(), trackRef.current._startDrag + delta));
    setDragX(next);
    if (next >= trackWidth() - 4) {
      setDragging(false);
      setStep(3);
    }
  };
  const onDragEnd = () => {
    setDragging(false);
    if (dragX < trackWidth() - 4) setDragX(0); // no llegó al final: regresa al inicio
  };

  const startHold = () => {
    setHolding(true);
    startRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.min(100, (elapsed / HOLD_MS) * 100);
      setHoldProgress(pct);
      if (pct >= 100) {
        clearInterval(timerRef.current);
        submit();
      }
    }, 50);
  };
  const cancelHold = () => {
    clearInterval(timerRef.current);
    setHolding(false);
    setHoldProgress(0);
  };

  const submit = async () => {
    setSubmitting(true);
    setErr("");
    try {
      await api.post(`/sos/${alertId}/mark-safe`, { booking_id: bookingId, group_id: groupId, sender_name: senderName });
      onResolved && onResolved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo confirmar. Intenta de nuevo.");
      setHolding(false);
      setHoldProgress(0);
      setStep(1);
      setChecked(false);
      setDragX(0);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="glass rounded-3xl p-6 border border-[var(--brand-cyan,#FF85AC)]/40 text-left max-w-md mx-auto mt-6" data-testid="mark-safe-flow">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="w-4 h-4 text-[var(--brand-cyan,#FF85AC)]" />
        <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--brand-cyan,#FF85AC)]">
          Marcar como seguro · paso {step} de 3
        </p>
      </div>

      {err && <p className="text-sm text-[#FF3B30] mb-4" data-testid="mark-safe-error">{err}</p>}

      {step === 1 && (
        <div className="fade-up">
          <label className="flex items-start gap-3 cursor-pointer mb-5" data-testid="mark-safe-checkbox-label">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              data-testid="mark-safe-checkbox"
              className="mt-1 w-4 h-4 accent-[var(--brand-cyan,#FF85AC)]"
            />
            <span className="text-sm text-neutral-300">
              Confirmo que estoy a salvo y ya no necesito ayuda de emergencia.
            </span>
          </label>
          <button
            onClick={() => checked && setStep(2)}
            disabled={!checked}
            data-testid="mark-safe-continue-btn"
            className="w-full bg-[var(--brand-cyan,#FF85AC)] text-black py-3 rounded-full font-semibold disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Continuar
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="fade-up">
          <p className="text-sm text-neutral-400 mb-4">Desliza hasta el final para seguir.</p>
          <div
            ref={trackRef}
            className="relative h-14 rounded-full bg-black/50 border border-white/15 select-none"
            style={{ touchAction: "none" }}
            onMouseMove={(e) => onDragMove(e.clientX)}
            onMouseUp={onDragEnd}
            onMouseLeave={onDragEnd}
            onTouchMove={(e) => onDragMove(e.touches[0].clientX)}
            onTouchEnd={onDragEnd}
          >
            <div className="absolute inset-0 flex items-center justify-center text-xs uppercase tracking-[0.25em] text-neutral-500 pointer-events-none">
              Desliza para continuar
            </div>
            <div
              onMouseDown={(e) => onDragStart(e.clientX)}
              onTouchStart={(e) => onDragStart(e.touches[0].clientX)}
              data-testid="mark-safe-slider-handle"
              className="absolute top-1 left-1 w-12 h-12 rounded-full bg-[var(--brand-cyan,#FF85AC)] flex items-center justify-center cursor-grab active:cursor-grabbing"
              style={{ transform: `translateX(${dragX}px)`, transition: dragging ? "none" : "transform 0.2s ease" }}
            >
              <ChevronsRight className="w-5 h-5 text-black" />
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="fade-up text-center">
          <p className="text-sm text-neutral-400 mb-5">Mantén presionado 3 segundos para confirmar.</p>
          <button
            onMouseDown={startHold}
            onMouseUp={cancelHold}
            onMouseLeave={cancelHold}
            onTouchStart={startHold}
            onTouchEnd={cancelHold}
            disabled={submitting}
            data-testid="mark-safe-hold-btn"
            className={`relative w-28 h-28 rounded-full mx-auto flex items-center justify-center transition-colors ${
              holding ? "bg-[var(--brand-cyan,#FF85AC)]/30 border-2 border-[var(--brand-cyan,#FF85AC)]" : "bg-black border border-[var(--brand-cyan,#FF85AC)]/60"
            }`}
            style={{ touchAction: "none" }}
          >
            {holding && (
              <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="47" fill="none" stroke="rgba(255,133,172,0.15)" strokeWidth="2" />
                <circle
                  cx="50" cy="50" r="47" fill="none"
                  stroke="var(--brand-cyan,#FF85AC)" strokeWidth="2.5" strokeLinecap="round"
                  strokeDasharray={`${(holdProgress * 295.3) / 100} 295.3`}
                />
              </svg>
            )}
            {submitting ? (
              <Loader2 className="w-8 h-8 text-[var(--brand-cyan,#FF85AC)] animate-spin" />
            ) : (
              <Check className="w-8 h-8 text-[var(--brand-cyan,#FF85AC)]" />
            )}
          </button>
        </div>
      )}
    </div>
  );
}
